import { toast } from "@credit-acceptance/uikit";
import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAddressVerificationGateStore } from "../../../../stores/addressVerificationGateStore";
import {
  ADDRESS_SLOTS,
  ADDRESS_VERIFICATION_RETRY_CAP,
  type AddressSlot,
  addressSlotKey,
  type AddressSnapshotFields,
  buildAddressVerificationRequest,
  buildStandardizedAddressFields,
  concatAddress,
  hasAddressChanged,
  isAddressValidForVerification,
  isBlockingStatus,
  normalizeVerificationStatus,
  type PersonPath,
  readSlotFields,
  type VerificationStatus,
} from "./addressVerification";
import { useVerifyAddress } from "./api/useVerifyAddress";
import type { CustomerInfoInput, SignersInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// useAddressVerificationGate
//
// Owns the 4-slot sequential Save gate described in address-standardization-
// spec.md §3/§7/§9: for each slot (driver physical -> driver mailing ->
// 2nd-signer physical -> 2nd-signer mailing), skip slots that are unverifiable
// or unchanged since the last successful verification, call the verify API for
// the rest, auto-accept `Ok`, and open AddressVerificationModal for any status
// that needs analyst action — blocking (and, on Cancel, aborting) Save until
// it's resolved.
//
// The per-slot "last verified" baseline is session-local bookkeeping (not a
// saved field, not persisted server-side) — see hasAddressChanged's doc
// comment for why it seeds from the hydrated address rather than a backend
// "last verified" record that doesn't exist yet.
//
// KNOWN LIMITATION: the baseline is captured once at this hook's mount and is
// never re-seeded afterward. useSignersForm.ts has its own resync effect that
// wholesale-replaces driver/secondSigner.customerInfo via setValue whenever
// the dashboard query refetches new content while the form is clean (e.g.
// after a Call Script save invalidates it) — if that resync ever brings in a
// genuinely different address than what this hook baselined at mount, the
// next Save will (harmlessly, but confusingly) treat it as analyst-changed
// and re-verify. Address text isn't expected to change via that resync path
// in practice today; revisit if a future dashboard refetch source starts
// touching it.
//
// Exposes `runGate` (registered into addressVerificationGateStore so
// ReviewPage's confirmAndSave — outside this form's tree — can call it) and
// the live `modalState` + handlers Signers.tsx renders AddressVerificationModal
// from.
// ---------------------------------------------------------------------------

interface UseAddressVerificationGateArguments {
  getValues: UseFormGetValues<SignersInput>;
  setValue: UseFormSetValue<SignersInput>;
  hasSecondSigner: boolean;
}

/** Live modal state for the slot currently awaiting analyst action (spec §7/§8). */
export interface AddressVerificationModalState {
  slot: AddressSlot;
  status: Extract<VerificationStatus, "WarningChanged" | "ErrorSubpremise" | "ErrorFailed">;
  entered: AddressSnapshotFields;
  suggested?: AddressSnapshotFields;
}

type ModalOutcome =
  | { action: "select"; fields: AddressSnapshotFields }
  | { action: "ignore" }
  | { action: "edit"; fields: AddressSnapshotFields }
  | { action: "cancel" };

interface SlotBaseline {
  /** The last-verified (or last-ignored) concatenated address (see concatAddress). */
  concat: string;
  /** True once the analyst has explicitly ignored a warning/error for this exact address (spec §7's "Ignore/override capability"). */
  ignored: boolean;
}

/**
 * Reads the given person's customerInfo off the form, or undefined for a 2nd
 * signer that isn't present on this deal.
 * @param {SignersInput} values - The form's current values.
 * @param {PersonPath} personPath - Which person to read.
 * @returns {CustomerInfoInput | undefined} That person's customerInfo, if present.
 */
function customerInfoFor(values: SignersInput, personPath: PersonPath): CustomerInfoInput | undefined {
  return personPath === "driver" ? values.driver.customerInfo : values.secondSigner?.customerInfo;
}

/**
 * Reads a slot's 6 address fields off the form's current values, delegating
 * the physical/mailing field mapping to addressVerification.ts's
 * readSlotFields (the single source of truth for that mapping).
 * @param {SignersInput} values - The form's current values.
 * @param {AddressSlot} slot - Which address slot to read.
 * @returns {AddressSnapshotFields | undefined} The slot's fields, or undefined for an absent 2nd signer.
 */
function readFields(values: SignersInput, slot: AddressSlot): AddressSnapshotFields | undefined {
  const customerInfo = customerInfoFor(values, slot.personPath);
  return customerInfo ? readSlotFields(customerInfo, slot.kind) : undefined;
}

/**
 * Writes a slot's 6 address fields back onto the form (auto-accept, or a confirmed modal selection).
 * @param {AddressSlot} slot - Which address slot to write.
 * @param {AddressSnapshotFields} fields - The (standardized or analyst-confirmed) address to write.
 * @param {UseFormSetValue<SignersInput>} setValue - The Signers form's RHF setValue.
 */
function applyFieldsToSlot(
  slot: AddressSlot,
  fields: AddressSnapshotFields,
  setValue: UseFormSetValue<SignersInput>
): void {
  const options = { shouldDirty: true } as const;
  if (slot.kind === "physical") {
    setValue(`${slot.personPath}.customerInfo.physicalStreetNumber`, fields.streetNumber, options);
    setValue(`${slot.personPath}.customerInfo.physicalStreetName`, fields.streetName, options);
    setValue(`${slot.personPath}.customerInfo.physicalApartmentNumber`, fields.apartmentNumber, options);
    setValue(`${slot.personPath}.customerInfo.physicalCity`, fields.city, options);
    setValue(`${slot.personPath}.customerInfo.physicalState`, fields.state, options);
    setValue(`${slot.personPath}.customerInfo.physicalZip`, fields.zip, options);
    return;
  }
  setValue(`${slot.personPath}.customerInfo.mailingStreetNumber`, fields.streetNumber, options);
  setValue(`${slot.personPath}.customerInfo.mailingStreetName`, fields.streetName, options);
  setValue(`${slot.personPath}.customerInfo.mailingApartmentNumber`, fields.apartmentNumber, options);
  setValue(`${slot.personPath}.customerInfo.mailingCity`, fields.city, options);
  setValue(`${slot.personPath}.customerInfo.mailingState`, fields.state, options);
  setValue(`${slot.personPath}.customerInfo.mailingZip`, fields.zip, options);
}

/**
 * Writes a verify (or ignore) outcome's Customer Hub id + USPS standardization
 * outcome back onto the form (spec open question #4). Called with
 * `(undefined, undefined)` whenever the address text moved on from what these
 * fields last described — an unverifiable/fail-open/network-error path never
 * got a fresh USPS answer, so any previously-recorded id/standardType would
 * otherwise misrepresent the (now different, still-unverified) address text.
 * @param {AddressSlot} slot - Which address slot to write.
 * @param {number | undefined} addressId - The verify response's Customer Hub address id, or undefined to clear it.
 * @param {string | undefined} standardType - The USPS standardization outcome to record (e.g. "GOOD"/"ERROR"), or undefined to clear it.
 * @param {UseFormSetValue<SignersInput>} setValue - The Signers form's RHF setValue.
 */
function applyVerificationMetaToSlot(
  slot: AddressSlot,
  addressId: number | undefined,
  standardType: string | undefined,
  setValue: UseFormSetValue<SignersInput>
): void {
  const options = { shouldDirty: true } as const;
  if (slot.kind === "physical") {
    setValue(`${slot.personPath}.customerInfo.physicalAddressId`, addressId, options);
    setValue(`${slot.personPath}.customerInfo.physicalAddressUspsStandardType`, standardType, options);
    return;
  }
  setValue(`${slot.personPath}.customerInfo.mailingAddressId`, addressId, options);
  setValue(`${slot.personPath}.customerInfo.mailingAddressUspsStandardType`, standardType, options);
}

/**
 * Builds the initial per-slot baseline from the form's current (hydrated) values.
 * @param {UseFormGetValues<SignersInput>} getValues - The Signers form's RHF getValues.
 * @returns {Record<string, SlotBaseline>} The initial baseline, keyed by addressSlotKey.
 */
function buildInitialBaselines(getValues: UseFormGetValues<SignersInput>): Record<string, SlotBaseline> {
  const values = getValues();
  const baselines: Record<string, SlotBaseline> = {};
  for (const slot of ADDRESS_SLOTS) {
    const fields = readFields(values, slot);
    if (fields) {
      baselines[addressSlotKey(slot)] = { concat: concatAddress(fields), ignored: false };
    }
  }
  return baselines;
}

/**
 * @param {UseAddressVerificationGateArguments} args - The Signers form's RHF getValues/setValue plus 2nd-signer presence.
 * @returns {object} `modalState` (undefined when no modal is open) and its action handlers, for Signers.tsx to render AddressVerificationModal.
 */
export function useAddressVerificationGate({
  getValues,
  setValue,
  hasSecondSigner,
}: UseAddressVerificationGateArguments) {
  const { t } = useTranslation();
  const verifyAddress = useVerifyAddress();
  const registerRunGate = useAddressVerificationGateStore((state) => state.registerRunGate);

  const baselinesReference = useRef<Record<string, SlotBaseline>>(buildInitialBaselines(getValues));
  const [modalState, setModalState] = useState<AddressVerificationModalState>();
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const modalResolveReference = useRef<((outcome: ModalOutcome) => void) | undefined>(undefined);

  /**
   * Records a slot's last-verified (or last-ignored) address as its new
   * baseline, and writes the matching Customer Hub id + USPS standardization
   * outcome alongside it — the two must always move together, since the
   * saved standardType/addressId are meaningless without knowing which
   * address text they describe (a prior finding: forgetting this on a
   * non-`Ok` path left stale metadata paired with newly-changed address text).
   * @param {AddressSlot} slot - Which address slot to update.
   * @param {AddressSnapshotFields} fields - The address the baseline should reflect.
   * @param {boolean} ignored - Whether this baseline was captured via an analyst "Ignore" (spec §7).
   * @param {number | undefined} addressId - The Customer Hub id this address now maps to, or undefined if none.
   * @param {string | undefined} standardType - The USPS standardization outcome this address now reflects, or undefined if unverified.
   */
  function updateBaseline(
    slot: AddressSlot,
    fields: AddressSnapshotFields,
    ignored: boolean,
    addressId: number | undefined,
    standardType: string | undefined
  ) {
    baselinesReference.current[addressSlotKey(slot)] = { concat: concatAddress(fields), ignored };
    applyVerificationMetaToSlot(slot, addressId, standardType, setValue);
  }

  /**
   * Re-baselines a person's 2 slots to a freshly-resynced customerInfo
   * (useSignersForm's dashboard-resync effect, via its onCustomerInfoResync
   * callback) — ground truth moved out from under this gate, so the new
   * values are the baseline, not a pending analyst edit to verify. Never
   * touches form state (that resync already did via its own setValue); only
   * updates this hook's own bookkeeping. Also clears any "ignored" flag —
   * a resync only ever applies while the form is clean (matches the last
   * save), so a stale ignore for the pre-resync text no longer applies.
   * @param {PersonPath} personPath - Which person's slots to re-baseline.
   * @param {CustomerInfoInput} customerInfo - The freshly-resynced customerInfo.
   */
  function reseedBaselines(personPath: PersonPath, customerInfo: CustomerInfoInput) {
    for (const slot of ADDRESS_SLOTS) {
      if (slot.personPath !== personPath) {
        continue;
      }
      const fields = readSlotFields(customerInfo, slot.kind);
      baselinesReference.current[addressSlotKey(slot)] = { concat: concatAddress(fields), ignored: false };
    }
  }

  /**
   * Opens the modal for a blocking status and waits for the analyst's action.
   * @param {AddressSlot} slot - Which address slot the modal is for.
   * @param {Extract<VerificationStatus, "WarningChanged" | "ErrorSubpremise" | "ErrorFailed">} status - The blocking status driving the modal's copy/layout.
   * @param {AddressSnapshotFields} entered - The "You Entered" address.
   * @param {AddressSnapshotFields | undefined} suggested - The "USPS Suggested" address (WarningChanged only).
   * @returns {Promise<ModalOutcome>} Resolves once the analyst picks an action.
   */
  function awaitModalResolution(
    slot: AddressSlot,
    status: Extract<VerificationStatus, "WarningChanged" | "ErrorSubpremise" | "ErrorFailed">,
    entered: AddressSnapshotFields,
    suggested: AddressSnapshotFields | undefined
  ): Promise<ModalOutcome> {
    return new Promise((resolve) => {
      modalResolveReference.current = resolve;
      setModalState({ slot, status, entered, suggested });
    });
  }

  /**
   * Resolves the pending modal Promise with the analyst's chosen outcome and closes the modal.
   * @param {ModalOutcome} outcome - The analyst's chosen action.
   */
  function resolveModal(outcome: ModalOutcome) {
    modalResolveReference.current?.(outcome);
    modalResolveReference.current = undefined;
    setModalState(undefined);
  }

  const onSelectAddress = useCallback(
    (fields: AddressSnapshotFields) => resolveModal({ action: "select", fields }),
    []
  );
  const onIgnore = useCallback(() => resolveModal({ action: "ignore" }), []);
  const onEditAddress = useCallback((fields: AddressSnapshotFields) => resolveModal({ action: "edit", fields }), []);
  const onCancelSave = useCallback(() => resolveModal({ action: "cancel" }), []);

  /**
   * Runs one slot to resolution: `true` to advance to the next slot / let Save
   * proceed, `false` to abort Save entirely (spec §7's Cancel / Edit-address
   * outcomes). Loops on itself (bounded by ADDRESS_VERIFICATION_RETRY_CAP) when
   * the analyst confirms an edited address that itself needs re-verification.
   * @param {AddressSlot} slot - Which address slot to run.
   * @returns {Promise<boolean>} Whether Save should proceed past this slot.
   */
  async function processSlot(slot: AddressSlot): Promise<boolean> {
    for (let attempt = 1; attempt <= ADDRESS_VERIFICATION_RETRY_CAP; attempt++) {
      const fields = readFields(getValues(), slot);
      if (!fields) {
        return true;
      }
      const key = addressSlotKey(slot);
      const baseline = baselinesReference.current[key];

      if (baseline?.ignored && concatAddress(fields) === baseline.concat) {
        // Already ignored, and untouched since — don't re-prompt (spec §7).
        return true;
      }
      if (!hasAddressChanged(baseline?.concat, fields)) {
        return true;
      }
      if (!isAddressValidForVerification(fields)) {
        // "Changed but unverifiable" (spec §4) — skip the API call, let Save
        // proceed. No verification happened for this address text, so any
        // previously-recorded id/standardType no longer applies — clear both.
        // eslint-disable-next-line unicorn/no-useless-undefined -- addressId/standardType are required params (not optional) here; explicit undefined means "clear", not "omit"
        updateBaseline(slot, fields, false, undefined, undefined);
        return true;
      }

      let response;
      const startedAt = Date.now();
      try {
        // eslint-disable-next-line no-await-in-loop -- a slot's retry loop is sequential by design (spec §9: re-verify, then re-evaluate before the next attempt)
        response = await verifyAddress.mutateAsync(buildAddressVerificationRequest(fields));
      } catch (error) {
        // Network/timeout/5xx — fail-open, matching legacy's swallow-and-continue (spec §7).
        // eslint-disable-next-line no-console -- telemetry (spec §10.2): warn on a fail-open event so a spike is visible
        console.warn("[addressVerification] verify call failed; fail-open", { slot: key, error });
        // The verify call never completed, so clear any stale id/standardType
        // rather than pairing it with this (possibly different) address text.
        // eslint-disable-next-line unicorn/no-useless-undefined -- addressId/standardType are required params (not optional) here; explicit undefined means "clear", not "omit"
        updateBaseline(slot, fields, false, undefined, undefined);
        return true;
      }
      const latencyMs = Date.now() - startedAt;
      const status = normalizeVerificationStatus(response.status);
      // eslint-disable-next-line no-console -- telemetry (spec §10.2): info log on every verify call (slot/status/latency)
      console.info("[addressVerification] verify", { slot: key, status, latencyMs });

      if (status === "Ok") {
        const standardized = buildStandardizedAddressFields(response, fields);
        applyFieldsToSlot(slot, standardized, setValue);
        updateBaseline(slot, standardized, false, response.addressId, "GOOD");
        return true;
      }
      if (status === "ErrorServiceFailed") {
        // eslint-disable-next-line no-console -- telemetry (spec §10.2): warn on a fail-open event so a spike is visible
        console.warn("[addressVerification] service failed; fail-open", { slot: key });
        // USPS/service itself failed — no verification result to record, so
        // clear any stale id/standardType rather than misattributing it.
        // eslint-disable-next-line unicorn/no-useless-undefined -- addressId/standardType are required params (not optional) here; explicit undefined means "clear", not "omit"
        updateBaseline(slot, fields, false, undefined, undefined);
        return true;
      }
      if (status === "ErrorBadCall") {
        // eslint-disable-next-line no-console -- telemetry (spec §10.2): error log — a client-bug indicator, not an address problem
        console.error("[addressVerification] bad call", { slot: key });
        toast.error(t("review.customer.residence.addressVerification.badCallError"));
        return false;
      }

      // status is one of the blocking statuses at this point — the last allowed
      // attempt forces WarningChanged down to the ErrorFailed (Ignore/Edit)
      // treatment so the analyst always has an escape hatch (spec §9).
      const effectiveStatus =
        attempt >= ADDRESS_VERIFICATION_RETRY_CAP && status === "WarningChanged" ? "ErrorFailed" : status;
      if (!isBlockingStatus(effectiveStatus)) {
        return true;
      }
      const suggested =
        effectiveStatus === "WarningChanged" ? buildStandardizedAddressFields(response, fields) : undefined;
      // eslint-disable-next-line no-await-in-loop -- awaiting the analyst's modal response before the next retry is the point of this loop
      const outcome = await awaitModalResolution(slot, effectiveStatus, fields, suggested);

      if (outcome.action === "cancel") {
        return false;
      }
      if (outcome.action === "ignore") {
        // The analyst overrode a USPS-flagged problem (ErrorSubpremise/
        // ErrorFailed — WarningChanged never reaches Ignore) — record ERROR
        // (not a stale/absent value) so downstream consumers can tell this
        // address was never USPS-confirmed, and clear addressId since there's
        // no Customer Hub id for an address USPS never validated.
        updateBaseline(slot, fields, true, undefined, "ERROR");
        return true;
      }
      if (outcome.action === "edit") {
        // Re-apply the "You Entered" address (no verification happened for
        // this text) and clear any stale id/standardType (same rule as every
        // other unverified-change path) — but deliberately DON'T advance the
        // baseline to it (unlike Ignore, which is a real resolution): Edit
        // means "not resolved yet," so the next Save must still see this
        // text as changed and re-verify it, not silently treat it as
        // already-handled because it happens to match a stale baseline.
        applyVerificationMetaToSlot(slot, undefined, undefined, setValue);
        applyFieldsToSlot(slot, outcome.fields, setValue);
        return false;
      }
      // "select" — apply the (already re-validated by the modal) chosen address and loop to re-verify it.
      applyFieldsToSlot(slot, outcome.fields, setValue);
    }
    // Retry cap exhausted without a resolved outcome — fail open rather than block Save indefinitely.
    return true;
  }

  const runGate = useCallback(async (): Promise<boolean> => {
    const slots = hasSecondSigner ? ADDRESS_SLOTS : ADDRESS_SLOTS.filter((slot) => slot.personPath === "driver");
    for (const slot of slots) {
      const values = getValues();
      const customerInfo = customerInfoFor(values, slot.personPath);
      // Mailing mirror rule (spec §3): while "Same as current" is checked,
      // mailing is a byte-for-byte copy of physical and isn't independently
      // verified — physical's own slot already covers it.
      if (slot.kind === "mailing" && customerInfo?.mailingSameAsCurrent) {
        continue;
      }
      // eslint-disable-next-line no-await-in-loop -- slots are verified sequentially by design (spec §3: Save blocks at the first slot needing analyst action)
      const proceed = await processSlot(slot);
      if (!proceed) {
        return false;
      }
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getValues/setValue/verifyAddress/t are stable-enough per render for this gate; re-running on every keystroke would break the retry-cap-per-Save-attempt contract
  }, [hasSecondSigner]);

  useEffect(() => {
    registerRunGate(runGate);
    return () => registerRunGate();
  }, [registerRunGate, runGate]);

  return { modalState, onSelectAddress, onIgnore, onEditAddress, onCancelSave, reseedBaselines };
}
